import { call, put, takeLatest } from "redux-saga/effects";

import {
  getAllStorageAction,
  setAllStorageAction,
  getSingleStorageAction,
  setSingleStorageAction,
  addStorageAction,
  setStorageResponse,
  setCreatedStorageResponse,
  updateStorageAction,
  setUpdateStorageResponse,
  deleteStorageAction,
  setDeleteStorageResponse,
  getStorageLogList,
  setStorageLogResponse,
} from "../reducers/storageSlice";

import { setLoaderAction, setNotificationAction } from "../reducers/mmymSlice";

import { callAPI, handleError, handleSuccess } from "../api/api";

/* -------------------------------------------------------------------------- */
/* API Config                                                                 */
/* -------------------------------------------------------------------------- */

const V2_CONFIG = {
  baseURL: process.env.REACT_APP_BASE_URL_V2,
};

/* -------------------------------------------------------------------------- */
/* Get All Storage                                                            */
/* -------------------------------------------------------------------------- */

function* getAllStorageSaga(action: { payload: any }): any {
  const {
    page = 1,
    limit = 10,
    orderBy = "createdAt",
    orderIn = "DESC",
    search = "",
    siteType = "",
    storageType = "",
    ...filters
  } = action.payload || {};

  yield put(setLoaderAction(true));

  try {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
      orderBy,
      orderIn,

      ...(search && {
        search: String(search),
      }),
      ...(siteType && {
        siteType: String(siteType),
      }),
      ...(storageType && {
        storageType: String(storageType),
      }),

      ...Object.entries(filters).reduce(
        (acc, [key, value]) => {
          if (value !== undefined && value !== null && value !== "") {
            acc[key] = String(value);
          }

          return acc;
        },
        {} as Record<string, string>,
      ),
    });

    const response = yield call(
      callAPI,
      {},
      "get",
      `storage/leads?${params.toString()}`,
      "",
      V2_CONFIG,
    );

    if (response?.data) {
      yield put(setAllStorageAction(response.data));
    }
  } catch (error: any) {
    console.error("Get All Storage Error:", error);

    if (error?.response) {
      handleError(error.response, [401]);
    }

    if (error?.code === "ERR_NETWORK") {
      handleError(error);
    }
  } finally {
    yield put(setLoaderAction(false));
  }
}

/* -------------------------------------------------------------------------- */
/* Get Single Storage                                                         */
/* -------------------------------------------------------------------------- */

function* getSingleStorageSaga(action: { payload: any }): any {
  console.log("action", action);
  const { storageId } = action.payload;

  yield put(setLoaderAction(true));

  try {
    const response = yield call(
      callAPI,
      {},
      "get",
      `storage/leads/${storageId}`,
      "",
      V2_CONFIG,
    );

    if (response?.data) {
      yield put(setSingleStorageAction(response.data));
    }
  } catch (error: any) {
    console.error("Get Single Storage Error:", error);

    if (error?.response) {
      handleError(error.response, [401]);
    }

    if (error?.code === "ERR_NETWORK") {
      handleError(error);
    }
  } finally {
    yield put(setLoaderAction(false));
  }
}

/* -------------------------------------------------------------------------- */
/* Add Storage                                                                */
/* -------------------------------------------------------------------------- */

function* addStorageSaga(action: { payload: any }): any {
  yield put(setLoaderAction(true));
  yield put(setNotificationAction({ message: [], severity: "error" }));
  const { batch, inceptBatch, leadDate, moveType, movingDate, ...newPayload } =
    action?.payload;
  try {
    const response = yield call(
      callAPI,
      {
        payload: newPayload,
      },
      "post",
      "storage/leads",
      "",
      V2_CONFIG,
    );

    if (response?.success === true) {
      handleSuccess(response);

      yield put(setCreatedStorageResponse(response?.data ?? null));
      yield put(setStorageResponse(true));
    }
  } catch (error: any) {
    console.error("Add Storage Error:", error);

    if (error?.response) {
      handleError(error.response);
    }

    if (error?.code === "ERR_NETWORK") {
      handleError(error);
    }
  } finally {
    yield put(setLoaderAction(false));
  }
}

/* -------------------------------------------------------------------------- */
/* Update Storage                                                             */
/* -------------------------------------------------------------------------- */

function* updateStorageSaga(action: { payload: any }): any {
  yield put(setLoaderAction(true));
  yield put(setNotificationAction({ message: [], severity: "error" }));
  try {
    const {
      id,
      batch,
      inceptBatch,
      leadDate,
      moveType,
      movingDate,
      ...payload
    } = action.payload || {};

    const response = yield call(
      callAPI,
      {
        payload,
      },
      "put",
      `storage/leads/${id}`,
      "",
      V2_CONFIG,
    );

    if (response?.success === true) {
      handleSuccess(response);

      yield put(setUpdateStorageResponse(true));
    }
  } catch (error: any) {
    console.error("Update Storage Error:", error);

    if (error?.response) {
      handleError(error.response);
    }

    if (error?.code === "ERR_NETWORK") {
      handleError(error);
    }
  } finally {
    yield put(setLoaderAction(false));
  }
}

/* -------------------------------------------------------------------------- */
/* Delete Storage                                                             */
/* -------------------------------------------------------------------------- */

function* deleteStorageSaga(action: { payload: any }): any {
  const storageId = action.payload;
  yield put(setLoaderAction(true));
  yield put(setNotificationAction({ message: [], severity: "error" }));

  try {
    const response = yield call(
      callAPI,
      {},
      "delete",
      `storage/${storageId}`,
      "",
      V2_CONFIG,
    );

    if (response?.success === true) {
      handleSuccess(response);

      yield put(setDeleteStorageResponse(true));
    }
  } catch (error: any) {
    console.error("Delete Storage Error:", error);

    if (error?.response) {
      handleError(error.response);
    }

    if (error?.code === "ERR_NETWORK") {
      handleError(error);
    }
  } finally {
    yield put(setLoaderAction(false));
  }
}

/* -------------------------------------------------------------------------- */
/* Get Single Storage  Logs                                                        */
/* -------------------------------------------------------------------------- */

function* getStorageLogListSaga(action: { payload: any }): any {
  console.log("action", action);
  const {
    page = 1,
    limit = 10,
    orderBy = "createdAt",
    orderIn = "DESC",
    leadId,
    contractId,
  } = action.payload;

  yield put(setLoaderAction(true));

  try {
    const response = yield call(
      callAPI,
      {},
      "get",
      `storage/logs?page=${page}&limit=${limit}&orderBy=${orderBy}&orderIn=${orderIn}&leadId=${leadId}&contractId=${contractId}`,
      "",
      V2_CONFIG,
    );

    if (response?.data) {
      yield put(setStorageLogResponse(response.data));
    }
  } catch (error: any) {
    console.error("Get Single Storage Error:", error);

    if (error?.response) {
      handleError(error.response, [401]);
    }

    if (error?.code === "ERR_NETWORK") {
      handleError(error);
    }
  } finally {
    yield put(setLoaderAction(false));
  }
}
/* -------------------------------------------------------------------------- */
/* Watcher                                                                    */
/* -------------------------------------------------------------------------- */

export function* watchStorage() {
  yield takeLatest(getAllStorageAction, getAllStorageSaga);
  yield takeLatest(getStorageLogList, getStorageLogListSaga);
  yield takeLatest(getSingleStorageAction, getSingleStorageSaga);

  yield takeLatest(addStorageAction, addStorageSaga);

  yield takeLatest(updateStorageAction, updateStorageSaga);

  yield takeLatest(deleteStorageAction, deleteStorageSaga);
}
